import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Lock, Menu, X } from 'lucide-react'
import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import './Landing.css'
import CrossroadsLogo from '../assets/images/full-logo-dark-mode.png'
import { NetworkGraph } from '../components/NetworkGraph'
import { VideoBackground } from '../components/VideoBackground'

import { AboutSection } from '@/components/landing/sections/AboutSection'
import { BentoGridSection } from '@/components/landing/sections/BentoGridSection'
import { CTASection } from '@/components/landing/sections/CTASection'
import { DataTypesSection } from '@/components/landing/sections/DataTypesSection'
import { FooterSection } from '@/components/landing/sections/FooterSection'
// Import all section components
import {
  HeroBottomSection,
  HeroSection,
} from '@/components/landing/sections/HeroSection'
import { IntegrationSection } from '@/components/landing/sections/IntegrationSection'

export const LandingShadcn = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [isPasswordProtected, setIsPasswordProtected] = useState(true)
  const [password, setPassword] = useState('')
  const [passwordError, setPasswordError] = useState('')

  const PREVIEW_PASSWORD = 'nodes&roads'

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (password === PREVIEW_PASSWORD) {
      setIsPasswordProtected(false)
      setPasswordError('')
    } else {
      setPasswordError('Incorrect password')
      setPassword('')
    }
  }

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Show password protection screen if not authenticated
  if (isPasswordProtected) {
    return (
      <div className='min-h-screen dark landing-mona relative flex items-center justify-center'>
        {/* Video Background */}
        <div className='fixed inset-0 z-0'>
          <VideoBackground />
          <div className='absolute inset-0 bg-gradient-to-b from-black/20 to-black/40' />
        </div>

        {/* Network Graph - Above blur effect */}
        <div className='fixed inset-0 z-10 pointer-events-none'>
          <NetworkGraph />
        </div>

        {/* Password Protection Form */}
        <div className='relative z-20 w-full max-w-md mx-4'>
          <div className='bg-[#2D284B]/90 backdrop-blur-xl rounded-2xl border border-[#9687F5]/20 shadow-2xl p-8'>
            <div className='text-center mb-8'>
              <img
                src={CrossroadsLogo}
                alt='Crossroads'
                className='h-16 w-auto mx-auto mb-6'
              />
              <div className='flex items-center justify-center mb-4'>
                <Lock className='w-8 h-8 text-[#9687F5] mr-3' />
                <h1 className='text-2xl font-bold text-white'>
                  Preview Access
                </h1>
              </div>
              <p className='text-gray-300'>
                This landing page is currently in development. Please enter the
                preview password to continue.
              </p>
            </div>

            <form onSubmit={handlePasswordSubmit} className='space-y-6'>
              <div>
                <Input
                  type='password'
                  placeholder='Enter preview password'
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className='w-full bg-[#1a1a2e] border-[#9687F5]/30 text-white placeholder-gray-400 focus:border-[#9687F5] focus:ring-[#9687F5]'
                />
                {passwordError && (
                  <p className='text-red-400 text-sm mt-2'>{passwordError}</p>
                )}
              </div>

              <Button
                type='submit'
                className='w-full bg-[#9687F5] hover:bg-[#8073e8] text-white py-3 text-lg font-semibold'
              >
                Access Preview
              </Button>
            </form>

            <div className='mt-6 text-center'>
              <Link
                to='/login'
                className='text-[#9687F5] hover:text-[#8073e8] text-sm'
              >
                Go to Login instead
              </Link>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='min-h-screen dark landing-mona relative'>
      {/* Video Background */}
      <div className='fixed inset-0 z-0'>
        <VideoBackground />
        <div className='absolute inset-0 bg-gradient-to-b from-black/5 to-black/15' />
      </div>

      {/* Network Graph - Above blur effect */}
      <div className='fixed inset-0 z-10 pointer-events-none'>
        <NetworkGraph />
      </div>

      {/* Navigation Header */}
      <header
        className={`sticky z-50 transition-all duration-500 ${
          scrolled ? 'top-4 mx-4 md:mx-8' : 'top-0 mx-0'
        }`}
      >
        <div
          className={`transition-all duration-500 ${
            scrolled
              ? 'bg-[#2D284B]/90 backdrop-blur-xl rounded-2xl border border-[#9687F5]/20 shadow-lg'
              : 'bg-transparent'
          }`}
        >
          <div
            className={`transition-all duration-500 ${
              scrolled ? 'px-6 py-3' : 'px-6 py-6'
            }`}
          >
            <div className='flex items-center justify-between'>
              <Link to='/' className='flex items-center'>
                <img
                  src={CrossroadsLogo}
                  alt='Crossroads'
                  className={`transition-all duration-500 ${
                    scrolled ? 'h-10 w-auto' : 'h-12 w-auto'
                  }`}
                />
              </Link>

              {/* Desktop Navigation */}
              <nav className='hidden md:flex items-center gap-8'>
                <Link
                  to='/about'
                  className={`text-foreground hover:text-white transition-all ${
                    scrolled ? 'text-base' : 'text-lg'
                  }`}
                >
                  About
                </Link>
                <Link
                  to='/platform'
                  className={`text-foreground hover:text-white transition-all ${
                    scrolled ? 'text-base' : 'text-lg'
                  }`}
                >
                  Platform
                </Link>
                <Link
                  to='/network'
                  className={`text-foreground hover:text-white transition-all ${
                    scrolled ? 'text-base' : 'text-lg'
                  }`}
                >
                  Network
                </Link>
                <Link
                  to='/features'
                  className={`text-foreground hover:text-white transition-all ${
                    scrolled ? 'text-base' : 'text-lg'
                  }`}
                >
                  Features
                </Link>
              </nav>

              <div className='flex items-center gap-4'>
                <Link to='/login'>
                  <Button
                    variant='ghost'
                    className={`text-foreground hover:text-white transition-all ${
                      scrolled ? 'text-base' : 'text-lg'
                    }`}
                  >
                    LOGIN
                  </Button>
                </Link>
                <Link to='/access'>
                  <Button
                    variant='default'
                    className={`bg-[#9687F5] hover:bg-[#8073e8] text-white transition-all ${
                      scrolled ? 'text-base px-4 py-2' : 'text-lg px-6 py-3'
                    }`}
                  >
                    GET ACCESS
                  </Button>
                </Link>

                {/* Mobile menu button */}
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className='md:hidden text-foreground'
                >
                  {mobileMenuOpen ? (
                    <X className='w-6 h-6' />
                  ) : (
                    <Menu className='w-6 h-6' />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div
            className={`md:hidden mt-2 rounded-2xl transition-all duration-500 ${
              scrolled
                ? 'bg-[#2D284B]/90 backdrop-blur-xl border border-[#9687F5]/20'
                : 'bg-[#2D284B]/80 backdrop-blur-xl border border-white/10'
            }`}
          >
            <nav className='px-6 py-4 flex flex-col gap-4'>
              <Link
                to='/about'
                className='text-foreground hover:text-white transition-colors'
              >
                About
              </Link>
              <Link
                to='/platform'
                className='text-foreground hover:text-white transition-colors'
              >
                Platform
              </Link>
              <Link
                to='/network'
                className='text-foreground hover:text-white transition-colors'
              >
                Network
              </Link>
              <Link
                to='/features'
                className='text-foreground hover:text-white transition-colors'
              >
                Features
              </Link>
              <Link
                to='/login'
                className='text-foreground hover:text-white transition-colors'
              >
                LOGIN
              </Link>
              <Link
                to='/access'
                className='text-foreground hover:text-white transition-colors'
              >
                GET ACCESS
              </Link>
            </nav>
          </div>
        )}
      </header>

      {/* Hero and Bottom Section Wrapper */}
      <div className='relative z-10'>
        <HeroSection />
        <HeroBottomSection />
      </div>

      {/* All Section Components */}
      <AboutSection />
      <DataTypesSection />
      {/* <IntegrationSection /> */}
      <BentoGridSection />
      <CTASection />
      <FooterSection />
    </div>
  )
}
